// AdminNotifications.tsx

import React, { useEffect, useState } from "react";
import { Send, X, Search } from "lucide-react";
import { toast } from "react-toastify";

import {
  getAllAdminNotificationsApi,
  sendCustomNotificationApi,
} from "../services/Notification/notification.service";

import {
  getAllBuyers,
  getAllSellers,
} from "../services/Users/User.service";

import "./notification.css";

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [buyers, setBuyers] = useState<any[]>([]);
  const [sellers, setSellers] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);

  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    userType: "buyer",
  });

  const [searchTerm, setSearchTerm] = useState("");

  // =========================
  // LOAD NOTIFICATIONS
  // =========================

  const loadNotifications = async () => {
    try {
      const res = await getAllAdminNotificationsApi();
      setNotifications(res?.data || []);
    } catch (error) {
      // console.log(error);
      setNotifications([]);
    }
  };

  // =========================
  // LOAD USERS
  // =========================

  const loadUsers = async () => {
    try {
      const buyersRes = await getAllBuyers();
      const sellersRes = await getAllSellers();

      setBuyers(buyersRes || []);
      setSellers(sellersRes || []);
    } catch (error) {
      // console.log(error);
      toast.error("Failed to load users");
    }
  };

  useEffect(() => {
    loadNotifications();
    loadUsers();
  }, []);

  // =========================
  // CURRENT USERS
  // =========================

const currentUsers =
  formData.userType === "buyer"
    ? Array.from(
        new Map(
          [...buyers, ...sellers].map((user) => [
            user.id,
            user,
          ])
        ).values()
      )
    : sellers;

  const filteredUsers = currentUsers.filter((user: any) => {
    const search = searchTerm.toLowerCase();

    return (
      `${user.firstName || ""} ${user.lastName || ""}`
        .toLowerCase()
        .includes(search) ||
      (user.mobileNumber || "")
        .toLowerCase()
        .includes(search) ||
      (user.businessName || "")
        .toLowerCase()
        .includes(search)
    );
  });

  // =========================
  // SELECT USER
  // =========================

  const handleUserSelect = (id: number) => {
    setSelectedUsers((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  // =========================
  // SELECT ALL
  // =========================

  const handleSelectAll = () => {
    const ids = filteredUsers.map(
      (user: any) => user.id
    );

    setSelectedUsers(ids);
  };

  // =========================
  // CLEAR ALL
  // =========================

  const handleClearAll = () => {
    setSelectedUsers([]);
  };

  // =========================
  // SEND NOTIFICATION
  // =========================

  const sendNotification = async () => {
    try {
      if (!formData.title.trim()) {
        toast.error("Title is required");
        return;
      }

      if (!formData.description.trim()) {
        toast.error("Description is required");
        return;
      }

      if (selectedUsers.length === 0) {
        toast.error("Please select users");
        return;
      }

      setLoading(true);

      const payload = {
        title: formData.title,
        description: formData.description,
        userIds: selectedUsers,
        userType: formData.userType,
      };

      const res = await sendCustomNotificationApi(
        payload
      );

      toast.success(
        res?.message ||
          "Notification sent successfully"
      );

      setShowModal(false);

      setFormData({
        title: "",
        description: "",
        userType: "buyer",
      });

      setSelectedUsers([]);

      setSearchTerm("");

      loadNotifications();
    } catch (error) {
      // console.log(error);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // USER TYPE CHANGE
  // =========================

  const handleUserTypeChange = (
    type: string
  ) => {
    setFormData({
      ...formData,
      userType: type,
    });

    setSelectedUsers([]);
    setSearchTerm("");
  };

  // =========================
  // USER DISPLAY NAME
  // =========================

  const getUserDisplayName = (user: any) => {
    const fullName =
      `${user.firstName || ""} ${
        user.lastName || ""
      }`.trim();

    if (fullName) {
      return `${fullName} (${user.mobileNumber || "No Mobile"
        })`;
    }

    if (user.businessName) {
      return `${user.businessName} (${user.mobileNumber || "No Mobile"
        })`;
    }

    return user.mobileNumber || "Unknown";
  };

  return (
    <div className="notification-page">

      {/* HEADER */}

      <div className="notification-header">

        <div>
          <h2 className="notification-title">
            Admin Notifications
          </h2>

          <p className="notification-subtitle">
            Send custom notifications to buyers & sellers
          </p>
        </div>

        <button
          className="notification-send-btn"
          onClick={() => setShowModal(true)}
        >
          <Send size={18} />
          Send Notification
        </button>

      </div>

      {/* TABLE */}

      <div className="notification-table-card">

        <table className="notification-table">

          <thead>
            <tr>
              <th>S.NO</th>
              <th>Title</th>
              <th>Description</th>
              <th>User Name </th>
              <th>Date</th>
            </tr>
          </thead>

          <tbody>

            {notifications.length > 0 ? (

              notifications.map(
                (item: any, index: number) => (

                  <tr key={item.id}>

                    <td>{index + 1}</td>

                    <td>{item.title}</td>

                    <td>{item.description}</td>

                    <td>
                      {item.user
                        ?.firstName ||
                        item.user
                          ?.businessName ||
                        "N/A"}
                    </td>

                    <td>
                      {new Date(
                        item.createdAt
                      ).toLocaleString()}
                    </td>

                  </tr>
                )
              )

            ) : (

              <tr>
                <td
                  colSpan={5}
                  className="empty-notification"
                >
                  No Notifications Found
                </td>
              </tr>

            )}

          </tbody>

        </table>

      </div>

      {/* MODAL */}

      {showModal && (

        <div className="notification-modal-overlay">

          <div className="notification-modal">

            {/* HEADER */}

            <div className="notification-modal-header">

              <h3>Send Notification</h3>

              <button
                className="close-modal-btn"
                onClick={() =>
                  setShowModal(false)
                }
              >
                <X size={18} />
              </button>

            </div>

            {/* BODY */}

            <div className="notification-modal-body">

              {/* USER TYPE */}

              <div className="notification-form-group">

                <label>
                  Select User Type
                </label>

                <div className="notification-radio-group">

                  <label className="notification-radio">

                    <input
                      type="radio"
                      checked={
                        formData.userType ===
                        "buyer"
                      }
                      onChange={() =>
                        handleUserTypeChange(
                          "buyer"
                        )
                      }
                    />

                    Buyer

                  </label>

                  <label className="notification-radio">

                    <input
                      type="radio"
                      checked={
                        formData.userType ===
                        "seller"
                      }
                      onChange={() =>
                        handleUserTypeChange(
                          "seller"
                        )
                      }
                    />

                    Seller

                  </label>

                </div>

              </div>

              {/* TITLE */}

              <div className="notification-form-group">

                <label>Title</label>

                <input
                  type="text"
                  placeholder="Enter notification title"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      title: e.target.value,
                    })
                  }
                />

              </div>

              {/* DESCRIPTION */}

              <div className="notification-form-group">

                <label>Description</label>

                <textarea
                  rows={4}
                  placeholder="Enter notification description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      description:
                        e.target.value,
                    })
                  }
                />

              </div>

              {/* SEARCH */}

              <div className="notification-form-group">

                <label>

                  Select{" "}
                  {formData.userType ===
                    "buyer"
                    ? "Buyers"
                    : "Sellers"}

                </label>

                <div className="search-input-wrapper">

                  <Search size={16} />

                  <input
                    type="text"
                    placeholder={`Search ${formData.userType}`}
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(
                        e.target.value
                      )
                    }
                  />

                </div>

              </div>

              {/* USER LIST */}

              <div className="notification-user-list">

                <div className="user-list-header">

                  <button
                    className="small-btn primary"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </button>

                  <button
                    className="small-btn secondary"
                    onClick={handleClearAll}
                  >
                    Clear
                  </button>

                </div>

                {filteredUsers.length > 0 ? (

                  filteredUsers.map((user: any) => (

                    <div
                      key={user.id}
                      className="notification-user-item"
                      onClick={() =>
                        handleUserSelect(user.id)
                      }
                    >

                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(
                          user.id
                        )}
                        readOnly
                      />

                      <span>
                        {getUserDisplayName(user)}
                      </span>

                    </div>
                  ))

                ) : (

                  <div className="empty-users">
                    No Users Found
                  </div>

                )}

              </div>

              <div className="selected-count">
                Selected Users:
                <span>
                  {selectedUsers.length}
                </span>
              </div>

            </div>

            {/* FOOTER */}

            <div className="notification-modal-footer">

              <button
                className="cancel-btn"
                onClick={() =>
                  setShowModal(false)
                }
              >
                Cancel
              </button>

              <button
                className="send-final-btn"
                onClick={sendNotification}
                disabled={loading}
              >
                {loading
                  ? "Sending..."
                  : "Send Notification"}
              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
};

export default AdminNotifications;